import { styled } from '@mui/material/styles';
import { Box, Button, Paper, Table, TableCell, TableHead, TableRow, Tabs, Typography } from '@mui/material';

// ============================================
// Layout
// ============================================
export const TabsWrapper = styled(Box)({
  marginTop: '12.5px',
  paddingBottom: 0,
  height: '40px',
});

export const Card = styled(Paper)({
  background: '#fff',
  borderRadius: '10px',
  padding: '30px 40px',
});

export const SearchRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const SearchGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const SearchLabel = styled(Typography)({
  marginRight: '20px',
  fontSize: '18px',
  fontWeight: 500,
  minWidth: '50px',
});

export const SearchButtonWrapper = styled(Box)({
  marginLeft: '4px',
});

// ============================================
// Tabs
// ============================================
export const StyledTabs = styled(Tabs)({
  '& .MuiTab-root.MuiTab-textColorSecondary': {
    borderRadius: '12px',
    border: '1px solid #4AB3BC',
    marginRight: '10px',
    minWidth: '100px',
    minHeight: '40px !important',
    padding: '12px 40px',
    fontWeight: 500,
    fontSize: '18px',
  },
  '& .MuiTab-root.MuiTab-textColorSecondary.Mui-selected': {
    background: '#4AB3BC',
    borderRadius: '12px',
    border: 'none',
    color: '#fff',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
});

// ============================================
// Table
// ============================================
export const StyledTableContainer = styled(Box)({
  border: '1px solid #ddd',
  overflow: 'auto',
});

export const StyledTable = styled(Table)({
  minWidth: 550,
});

export const StyledTableHead = styled(TableHead)({
  background: '#fafbfc',
});

export const HeaderCell = styled(TableCell)({
  fontSize: '12px',
  fontWeight: 500,
  borderRight: '1px solid #ddd',
  '&:last-child': {
    borderRight: 'none',
  },
});

export const BodyRow = styled(TableRow)({
  cursor: 'pointer',
  textDecoration: 'none',
  '&:last-child td, &:last-child th': { borderBottom: 0 },
  '&:hover': { background: '#f0f0f0' },
});

export const BodyCell = styled(TableCell)({
  borderRight: '1px solid #ddd',
  '&:last-child': {
    borderRight: 'none',
  },
});

export const NameCellInner = styled(Box)({
  display: 'flex',
  gap: '10px',
  justifyContent: 'center',
});

export const PendingDot = styled(Box)({
  width: '8px',
  height: '8px',
  background: '#ff0000',
  borderRadius: '50%',
});

export const NameText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isPending',
})(({ isPending }) => ({
  fontWeight: isPending ? 800 : 400,
}));

// ============================================
// Approve Button (행 단위)
// ============================================
export const ApproveButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isPending',
})(({ isPending }) => ({
  padding: '12px 25px',
  borderRadius: '5px',
  height: '36px',
  background: isPending ? '#0F7F85' : '#98A6AD',
  color: '#fff',
  '&:hover': {
    background: isPending ? '#0F7F85' : '#ddd',
    color: '#fff',
  },
}));

// ============================================
// 양도 탭 - 보유 수강권/이용권 테이블 + 양도 버튼
// ============================================
export const SectionTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#262626',
  marginBottom: '12px',
});

export const SubSectionTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#404040',
  marginTop: '12px',
  marginBottom: '12px',
});

// 행 단위 "양도" 버튼 (teal). 비활성 시 회색.
export const TransferRowButton = styled(Button)({
  padding: '6px 18px',
  borderRadius: '6px',
  height: '32px',
  fontSize: '13px',
  background: '#4AB3BC',
  color: '#fff',
  boxShadow: 'none',
  '&:hover': {
    background: '#3a9aa2',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    background: '#d9d9d9',
    color: '#fff',
  },
});

export const EmptyRowText = styled(Typography)({
  fontSize: '13px',
  color: '#a6a6a6',
  textAlign: 'center',
  padding: '32px 0',
});

// ============================================
// 양도 모달 - 폼 레이아웃
// ============================================
export const ModalSectionLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
  marginBottom: '8px',
});

export const FieldRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '14px',
});

export const FieldLabel = styled(Typography)({
  fontSize: '14px',
  color: '#404040',
  marginRight: '20px',
  minWidth: '90px',
});

// 회원검색 결과 테이블
export const SearchResultScroll = styled(Box)({
  maxHeight: '200px',
  overflowY: 'auto',
  border: '1px solid #ddd',
  borderRadius: '6px',
  marginBottom: '14px',
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: '10px' },
});

export const ResultRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected',
})(({ selected }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 10px',
  fontSize: '12px',
  cursor: 'pointer',
  borderBottom: '1px solid #eee',
  background: selected ? '#e9f6f7' : '#fff',
  '&:hover': { background: selected ? '#e9f6f7' : '#f5f5f5' },
  '&:last-of-type': { borderBottom: 'none' },
}));

export const ResultCell = styled(Box, {
  shouldForwardProp: prop => prop !== 'flexValue',
})(({ flexValue }) => ({
  flex: flexValue || 1,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  padding: '0 4px',
}));

export const ResultHeadRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 10px',
  fontSize: '12px',
  fontWeight: 600,
  background: '#fafbfc',
  borderBottom: '1px solid #ddd',
  position: 'sticky',
  top: 0,
  zIndex: 1,
});

export const RadioRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
});

// ============================================
// 양도 모달 - 피그마 재현용 (회원검색 가로라벨 / 2단 / 환불수단 토글 / 금액+원)
// ============================================

// 회원 검색 한 줄 (라벨 왼쪽 + 입력창)
export const SearchInlineRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  marginBottom: '14px',
});

// 입력창 왼쪽 라벨 (우측정렬)
export const SearchInlineLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#404040',
  minWidth: '72px',
  textAlign: 'right',
  whiteSpace: 'nowrap',
});

// 2단 가로 묶음 (담당자|수수료 / 수수료|미수금 등)
export const TwoColRow = styled(Box)({
  display: 'flex',
  gap: '16px',
  width: '100%',
  marginBottom: '16px',
});

export const ColGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: 1,
  minWidth: 0,
});

// 2단/단독 필드 라벨
export const FieldGroupLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#141414',
});

// 결제 영역 박스 (양도수수료~카드정보를 감싸는 회색 테두리 박스)
export const PaidSection = styled(Box)({
  border: '1px solid #e5e5e5',
  borderRadius: '8px',
  padding: '20px 16px',
  marginBottom: '16px',
});

// 금액 입력 + 우측 "원" 단위 래퍼
export const AmountInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  height: '44px',
  border: '1px solid #a6a6a6',
  borderRadius: '4px',
  padding: '0 16px',
  backgroundColor: '#fff',
});

export const AmountUnit = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#737373',
  flexShrink: 0,
});

// 환불수단 토글 버튼 그룹
export const ToggleGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
  width: '100%',
});

export const ToggleButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  flex: 1,
  height: '52px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  borderRadius: '4px',
  border: active ? '1px solid #262626' : '1px solid #d9d9d9',
  backgroundColor: active ? '#262626' : '#fff',
  color: active ? '#fff' : '#404040',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: active ? '#262626' : '#f5f5f5',
  },
}));

// 결과 테이블 빈 영역 (피그마: 헤더만 있고 본문 비어있는 박스)
export const ResultEmptyBox = styled(Box)({
  height: '52px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  color: '#a6a6a6',
});

// 하단 버튼 중앙 정렬 래퍼 (Footer가 flex-end라 내부에서 중앙 정렬)
export const FooterCenter = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
});

// 회원검색 미리보기 — 입력창 아래 간략 후보 리스트 (개인레슨/상품판매 검색 톤)
export const PreviewList = styled(Box)({
  maxHeight: '180px',
  overflowY: 'auto',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  marginTop: '8px',
  marginBottom: '14px',
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: '10px' },
});

export const PreviewItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected',
})(({ selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '10px 14px',
  fontSize: '13px',
  cursor: 'pointer',
  borderBottom: '1px solid #f0f0f0',
  background: selected ? '#e9f6f7' : '#fff',
  '&:hover': { background: selected ? '#e9f6f7' : '#f5f5f5' },
  '&:last-of-type': { borderBottom: 'none' },
}));

export const PreviewName = styled('span')({
  fontWeight: 600,
  color: '#141414',
});

export const PreviewTag = styled('span')({
  fontSize: '12px',
  color: '#4AB3BC',
  fontWeight: 600,
});

export const PreviewPhone = styled('span')({
  color: '#737373',
});

export const PreviewEmpty = styled(Box)({
  padding: '14px',
  fontSize: '13px',
  color: '#a6a6a6',
  textAlign: 'center',
});

// 선택된 회원 요약 박스 (지점/성별/생년월일을 선택 후 보여줌)
export const SelectedSummary = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '6px 14px',
  padding: '12px 14px',
  marginBottom: '14px',
  border: '1px solid #4AB3BC',
  borderRadius: '8px',
  background: '#f3f9fa',
  fontSize: '13px',
  color: '#404040',
});

export const SummaryName = styled('span')({
  fontWeight: 700,
  color: '#0F7F85',
});

export const SummaryDivider = styled('span')({
  color: '#cde7e9',
});

// ============================================
// 양도 모달 섹션 구조 — 양도할 이용권 / 양수자 / 라커 자리 / 수수료 / 메모 (2026-10-13)
// ============================================

// 섹션 하나 — 섹션 사이 간격을 한 곳에서 맞춘다
export const TransferSection = styled(Box)({
  marginBottom: '24px',
});

export const TransferSectionLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#141414',
  marginBottom: '8px',
});

// 섹션 아래 보조 안내 (왼쪽 정렬, 회색)
export const TransferHelperText = styled(Typography)({
  fontSize: '12.5px',
  color: '#8C8C8C',
  marginTop: '6px',
  lineHeight: 1.5,
});

// 양도할 이용권 카드 / 선택된 양수자 카드 공용
export const TransferInfoCard = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '6px 12px',
  padding: '12px 14px',
  border: '1px solid #E5E5E5',
  borderRadius: '8px',
  background: '#FAFAFA',
  fontSize: '13px',
  color: '#595959',
});

export const TransferInfoName = styled('span')({
  fontSize: '14px',
  fontWeight: 700,
  color: '#141414',
});
